import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ExternalLink, Check } from "lucide-react";

const APPS = {
    design: {
        name: "Design & Stampa",
        tagline: "Idee che diventano carta, vinile e luce.",
        subtitle: "Il braccio creativo e produttivo di TotalKlick. Brand identity, stampa di grande formato, allestimenti per eventi, retail e interni. Dalla matita al cantiere — un solo interlocutore.",
        logo: "/brand/design/logo.png",
        primary: "#06B6D4", accent: "#E53935",
        url: null,
        cta: "Richiedi un preventivo",
        features: [
            { t: "🎨 Brand identity", d: "Logo, palette, manuale d'uso, mood board. La tua identità visiva da zero o restyling." },
            { t: "🖨️ Stampa grande formato", d: "Adesivi, vinili, PVC, forex, cartelloni 6×3, lonari, banner. Solventi e UV." },
            { t: "🏷️ Materiale pubblicitario", d: "Volantini, brochure, biglietti da visita, carta intestata, packaging. Tirature da 50 a 50.000." },
            { t: "🪧 Insegne & decorazione", d: "Insegne luminose, vetrofanie, wayfinding, decorazione muraria, scritte 3D." },
            { t: "🎪 Allestimenti", d: "Eventi, fiere, store opening, vetrine stagionali. Progettazione + produzione + montaggio." },
            { t: "📦 Service completo", d: "Un solo contatto dalla bozza alla consegna. Tempi di stampa rapidi, ritiro o consegna." },
        ],
        ideal: ["Negozi & retail", "Studi professionali", "Eventi & fiere", "Ristoranti & bar", "Aziende & PMI"],
    },
};

export default function AppPresentation() {
    const { app } = useParams();
    const data = APPS[app];
    if (!data) return <Navigate to="/" replace />;
    const { name, tagline, subtitle, logo, heroImage, primary, accent, url, cta, features, ideal } = data;
    const isInternalCta = !url; // design / pages without external URL → link to home contact

    return (
        <div style={{ fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif', background: "#fff", color: "#0A1730" }}>
            <header className="px-6 md:px-12 py-4 flex items-center justify-between sticky top-0 z-50 backdrop-blur-md" style={{ background: "rgba(255,255,255,0.95)", borderBottom: "1px solid #E6ECFA" }}>
                <Link to="/" className="flex items-center gap-2 text-[13px] font-bold hover:opacity-70" data-testid="app-back-home"><ArrowLeft size={14} /> <span>TotalKlick</span></Link>
                {url ? (
                    <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-extrabold text-[13px] text-white" style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }} data-testid="app-header-cta">{cta}</a>
                ) : (
                    <Link to="/#contatti" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-extrabold text-[13px] text-white" style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }} data-testid="app-header-cta">{cta}</Link>
                )}
            </header>

            {/* HERO */}
            <section className="px-6 md:px-12 py-20 md:py-28" style={{ background: `linear-gradient(135deg, ${primary}08, ${accent}05)` }}>
                <div className="max-w-5xl mx-auto text-center">
                    {heroImage ? (
                        <div className="flex flex-col items-center mb-6 gap-3">
                            <img src={heroImage} alt={`${name} character`} className="h-48 md:h-64 object-contain drop-shadow-2xl" data-testid="app-hero-image" />
                            <img src={logo} alt={name} className="h-12 md:h-16 object-contain" data-testid="app-logo" />
                        </div>
                    ) : (
                        <img src={logo} alt={name} className="h-20 md:h-28 mx-auto mb-8 object-contain" data-testid="app-logo" />
                    )}
                    <h1 className="text-[40px] md:text-[64px] font-extrabold tracking-tighter leading-[1.05]" data-testid="app-title">{tagline}</h1>
                    <p className="mt-6 text-[16px] md:text-[19px] max-w-3xl mx-auto" style={{ color: "#5C6B8C" }}>{subtitle}</p>
                    {url ? (
                        <a href={url} target="_blank" rel="noreferrer" className="mt-10 inline-flex items-center gap-2 px-7 py-4 rounded-full font-extrabold text-[15px] text-white" style={{ background: `linear-gradient(135deg, ${primary}, ${accent})`, boxShadow: `0 16px 36px ${primary}40` }} data-testid="app-hero-cta">
                            {cta} <ExternalLink size={15} />
                        </a>
                    ) : (
                        <Link to="/#contatti" className="mt-10 inline-flex items-center gap-2 px-7 py-4 rounded-full font-extrabold text-[15px] text-white" style={{ background: `linear-gradient(135deg, ${primary}, ${accent})`, boxShadow: `0 16px 36px ${primary}40` }} data-testid="app-hero-cta">
                            {cta} <ArrowRight size={15} />
                        </Link>
                    )}
                </div>
            </section>

            {/* FEATURES */}
            <section className="px-6 md:px-12 py-20" style={{ background: "#0A1730", color: "#fff" }}>
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-center text-[30px] md:text-[44px] font-extrabold tracking-tighter mb-12">Cosa fa {name}</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {features.map((f, i) => (
                            <div key={i} className="p-6 rounded-2xl" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }} data-testid={`app-feature-${i}`}>
                                <div className="text-[28px] mb-3">{f.t.split(" ")[0]}</div>
                                <h3 className="font-extrabold text-[17px] mb-2">{f.t.split(" ").slice(1).join(" ")}</h3>
                                <p className="text-[13.5px] opacity-80 leading-relaxed">{f.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* IDEAL FOR */}
            <section className="px-6 md:px-12 py-16">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-[24px] md:text-[32px] font-extrabold tracking-tight mb-2">Pensato per</h2>
                    <p className="text-[14px] mb-8" style={{ color: "#5C6B8C" }}>Lavoriamo con questi settori ogni giorno.</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                        {ideal.map((it, i) => (
                            <span key={i} className="px-4 py-2 rounded-full text-[13px] font-bold" style={{ background: `${primary}11`, color: primary, border: `1px solid ${primary}33` }} data-testid={`app-ideal-${i}`}>
                                <Check size={12} className="inline mr-1.5" />{it}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA FINAL */}
            {(url || isInternalCta) && (
                <section className="px-6 md:px-12 py-20" style={{ background: `linear-gradient(135deg, ${primary}, ${accent})`, color: "#fff" }}>
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-[34px] md:text-[48px] font-extrabold tracking-tighter mb-4">{url ? "Provalo subito." : "Pronti a partire?"}</h2>
                        <p className="text-[16px] mb-8 opacity-90">
                            {url
                                ? `${name} è online. Vai sul sito ufficiale e prova in autonomia, oppure scrivi a TotalKlick per parlare con un consulente.`
                                : `Raccontaci il tuo progetto. Un nostro consulente ti contatta entro 24h con una proposta concreta.`}
                        </p>
                        <div className="flex flex-wrap gap-3 justify-center">
                            {url ? (
                                <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-extrabold text-[15px]" style={{ background: "#fff", color: primary }} data-testid="app-cta-bottom">
                                    {cta} <ExternalLink size={15} />
                                </a>
                            ) : (
                                <Link to="/#contatti" className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-extrabold text-[15px]" style={{ background: "#fff", color: primary }} data-testid="app-cta-bottom">
                                    {cta} <ArrowRight size={15} />
                                </Link>
                            )}
                            <Link to="/#contatti" className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-bold text-[14px]" style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)" }}>
                                Parla con noi <ArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* Mini footer */}
            <footer className="px-6 py-8 text-center" style={{ background: "#06112A", color: "#94A3B8" }}>
                <Link to="/" className="text-[12px] font-bold hover:text-white">← Torna alla home TotalKlick</Link>
                <div className="text-[10.5px] mt-2 mono uppercase tracking-wider opacity-60">© {new Date().getFullYear()} TotalKlick® srls · {name}</div>
            </footer>
        </div>
    );
}
